"use client";

/**
 * A contact's photo, or — when there is none, or it fails to load — their
 * initials on a deterministic brand colour. The one avatar every contact
 * surface renders (built on Hub's Radix `Avatar`, which swaps to the fallback
 * by itself while an image loads or after it errors).
 *
 * `avatarUrl` is `contacts.avatar_url`: normally a same-origin Hub path
 * (`/api/contacts/<id>/avatar?v=…`, served behind the viewer's session); a
 * legacy `https:` URL still renders, anything else shows initials
 * (`renderableAvatarSrc`).
 */

import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { renderableAvatarSrc } from "@/lib/contacts/avatar/avatar-path";
import { avatarBackgroundFor, contactInitials } from "@/lib/contacts/avatar/initials";

export type ContactAvatarSize = "xs" | "sm" | "md" | "lg";

const SIZE_CLASSES: Record<ContactAvatarSize, { root: string; text: string }> = {
  xs: { root: "size-6", text: "text-[10px]" },
  sm: { root: "size-8", text: "text-xs" },
  md: { root: "size-10", text: "text-sm" },
  lg: { root: "size-14 md:size-16", text: "text-lg md:text-xl" },
};

export interface ContactAvatarProps {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  /** Stable colour seed — pass the contact id when there is one (a rename keeps the colour). */
  seed?: string | null;
  size?: ContactAvatarSize;
  className?: string;
  /**
   * When the name is already visible beside the avatar (the usual case) the
   * avatar is decorative and hidden from assistive tech. Set false where the
   * avatar stands alone (e.g. a chip row) so it is announced by name.
   */
  decorative?: boolean;
}

export function ContactAvatar({
  name,
  email,
  avatarUrl,
  seed,
  size = "sm",
  className,
  decorative = true,
}: ContactAvatarProps) {
  const src = renderableAvatarSrc(avatarUrl);
  const initials = contactInitials(name, email);
  const background = avatarBackgroundFor(seed || name || email);
  const label = name?.trim() || email?.trim() || "Contact";
  const classes = SIZE_CLASSES[size];

  return (
    <Avatar
      className={cn(classes.root, className)}
      data-testid="contact-avatar"
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": label })}
    >
      {src ? (
        <AvatarImage
          src={src}
          alt=""
          className="object-cover"
          // A legacy third-party URL must not learn which Hub page asked for it.
          referrerPolicy="no-referrer"
          draggable={false}
        />
      ) : null}
      <AvatarFallback
        className={cn("font-semibold tracking-wide text-white", classes.text)}
        style={{ backgroundColor: background }}
        data-testid="contact-avatar-initials"
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
